/**
 * Stop Time Heatmap View
 * Visar områden/platser sorterade efter stopptid med visuell prioritetsindikering
 */

import React, { useState, useMemo } from 'react';
import { StopTimeAnalysisResult, StopTimeLocation } from '@/types/analysis';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface StopTimeHeatmapProps {
  data: StopTimeAnalysisResult;
}

type SortMode = 'hours' | 'stops' | 'vehicles' | 'efficiency';

const StopTimeHeatmap: React.FC<StopTimeHeatmapProps> = ({ data }) => {
  const [sortMode, setSortMode] = useState<SortMode>('hours');
  const [selectedLocation, setSelectedLocation] = useState<StopTimeLocation | null>(null);

  // Sortera locations
  const sortedLocations = useMemo(() => {
    const sorted = [...data.locations];
    switch (sortMode) {
      case 'hours':
        sorted.sort((a, b) => b.stopTimeHours - a.stopTimeHours);
        break;
      case 'stops':
        sorted.sort((a, b) => b.stopCount - a.stopCount);
        break;
      case 'vehicles':
        sorted.sort((a, b) => b.vehicleCount - a.vehicleCount);
        break;
      case 'efficiency':
        sorted.sort((a, b) => b.hoursPerStop - a.hoursPerStop);
        break;
    }
    return sorted;
  }, [data.locations, sortMode]);

  // Färg baserat på prioritet
  const getColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-500';
      case 'high':
        return 'bg-orange-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Prioritet text
  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'Kritisk';
      case 'high':
        return 'Hög';
      case 'medium':
        return 'Medel';
      case 'low':
        return 'Låg';
      default:
        return 'Okänd';
    }
  };

  // Rank badge färg
  const getRankBadgeClass = (index: number) => {
    if (index === 0) return 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-gray-900';
    if (index === 1) return 'bg-gradient-to-br from-gray-300 to-gray-500 text-gray-900';
    if (index === 2) return 'bg-gradient-to-br from-orange-600 to-orange-800 text-white';
    return 'bg-bgSecondary text-textMuted';
  };

  return (
    <div className="space-y-6">
      {/* Sort Controls */}
      <div className="flex space-x-2">
        <button
          onClick={() => setSortMode('hours')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            sortMode === 'hours'
              ? 'bg-blue-600 text-white'
              : 'bg-bgElevated text-textSecondary hover:bg-bgHover'
          }`}
        >
          Sortera: Stopptid
        </button>
        <button
          onClick={() => setSortMode('stops')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            sortMode === 'stops'
              ? 'bg-blue-600 text-white'
              : 'bg-bgElevated text-textSecondary hover:bg-bgHover'
          }`}
        >
          Sortera: Antal stopp
        </button>
        <button
          onClick={() => setSortMode('vehicles')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            sortMode === 'vehicles'
              ? 'bg-blue-600 text-white'
              : 'bg-bgElevated text-textSecondary hover:bg-bgHover'
          }`}
        >
          Sortera: Antal fordon
        </button>
        <button
          onClick={() => setSortMode('efficiency')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            sortMode === 'efficiency'
              ? 'bg-blue-600 text-white'
              : 'bg-bgElevated text-textSecondary hover:bg-bgHover'
          }`}
        >
          Sortera: Timmar/stopp
        </button>
      </div>

      {/* Heatmap Grid */}
      <div className="bg-bgElevated rounded-lg p-4 border border-border">
        <div className="text-sm font-medium text-textSecondary mb-4">
          🔥 Områden med högst stopptid (klicka för detaljer)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {sortedLocations.slice(0, 24).map((location) => (
            <div
              key={location.locationId}
              onClick={() => setSelectedLocation(location)}
              className={`${getColor(location.priority)} rounded-lg p-3 cursor-pointer transition-transform hover:scale-105 hover:shadow-lg text-white`}
            >
              <div className="text-xs font-semibold mb-1 line-clamp-2">
                {location.name}
              </div>
              <div className="text-lg font-bold">
                {location.stopTimeDays.toFixed(0)} <span className="text-xs">dagar</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-textSecondary">
          <span>Låg</span>
          <div className="flex-1 h-3 rounded-full bg-gradient-to-r from-green-500 via-yellow-500 via-orange-500 to-red-500"></div>
          <span>Hög prioritet</span>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedLocation && (
        <div className="bg-bgElevated rounded-lg p-6 border border-border animate-in slide-in-from-bottom-4">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-semibold text-textDefault">
              {selectedLocation.name}
            </h3>
            <button
              onClick={() => setSelectedLocation(null)}
              className="text-textSecondary hover:text-textDefault"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-4 bg-bgContainer rounded-lg">
              <div className="text-2xl font-bold text-yellow-500">
                {selectedLocation.stopTimeDays.toFixed(0)}
              </div>
              <div className="text-xs text-textSecondary mt-1">Dagar total stopptid</div>
            </div>
            <div className="text-center p-4 bg-bgContainer rounded-lg">
              <div className="text-2xl font-bold text-yellow-500">
                {selectedLocation.stopCount.toLocaleString('sv-SE')}
              </div>
              <div className="text-xs text-textSecondary mt-1">Antal stopp</div>
            </div>
            <div className="text-center p-4 bg-bgContainer rounded-lg">
              <div className="text-2xl font-bold text-yellow-500">
                {selectedLocation.vehicleCount}
              </div>
              <div className="text-xs text-textSecondary mt-1">Fordon</div>
            </div>
          </div>
          {selectedLocation.recommendation && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-green-600 dark:text-green-400">✓</span>
                <p className="text-sm text-green-800 dark:text-green-200">
                  {selectedLocation.recommendation}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-bgElevated rounded-lg border border-border overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3 text-xs font-medium text-textSecondary uppercase">#</th>
              <th className="text-left p-3 text-xs font-medium text-textSecondary uppercase">Område/Plats</th>
              <th className="text-left p-3 text-xs font-medium text-textSecondary uppercase">Stopptid (dagar)</th>
              <th className="text-left p-3 text-xs font-medium text-textSecondary uppercase">Antal stopp</th>
              <th className="text-left p-3 text-xs font-medium text-textSecondary uppercase">Fordon</th>
              <th className="text-left p-3 text-xs font-medium text-textSecondary uppercase">Tim/stopp</th>
              <th className="text-left p-3 text-xs font-medium text-textSecondary uppercase">Prioritet</th>
            </tr>
          </thead>
          <tbody>
            {sortedLocations.map((location, index) => (
              <tr
                key={location.locationId}
                onClick={() => setSelectedLocation(location)}
                className="border-b border-border hover:bg-bgHover cursor-pointer transition-colors"
              >
                <td className="p-3">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-semibold ${getRankBadgeClass(index)}`}>
                    {index + 1}
                  </span>
                </td>
                <td className="p-3 font-medium text-textDefault">{location.name}</td>
                <td className="p-3 font-mono text-textDefault">{location.stopTimeDays.toFixed(1)}</td>
                <td className="p-3 font-mono text-textDefault">
                  {location.stopCount.toLocaleString('sv-SE')}
                </td>
                <td className="p-3 font-mono text-textDefault">{location.vehicleCount}</td>
                <td className="p-3 font-mono text-textDefault">{location.hoursPerStop.toFixed(1)}h</td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1`}>
                    <span className={`w-2 h-2 rounded-full ${
                      location.priority === 'critical' ? 'bg-red-500' :
                      location.priority === 'high' ? 'bg-orange-500' :
                      location.priority === 'medium' ? 'bg-yellow-500' :
                      'bg-green-500'
                    }`}></span>
                    {getPriorityText(location.priority)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StopTimeHeatmap;

